import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Grid,
  Typography,
  Breadcrumbs,
  Box,
} from '@mui/material';

import { ReactComponent as ExerciseLogo } from '../../assets/DRT/Icon_Exercise.svg';
import { ReactComponent as TestLogo } from '../../assets/DRT/Icon_Readout-Test.svg';
import { ReactComponent as ManualLogo } from '../../assets/DRT/Icon_Anleitung.svg';
import ContentBox from '../../components/layout/ContentBox';
import SelectionCard, { CardIdType } from '../../components/layout/SelectionCard';
import { useAppDispatch } from '../../hooks/reduxHooks';
import { fetchTrial } from '../../redux/actions';
import {
  DigitalSurvey, Enrollment, RootState,
} from '../../redux/types';
import ApiClient from '../../loopback/apiClient';
import { downloadFromContainer } from '../../utilities/urlHelper';
import { MANUALS_CONTAINER_NAME } from '../../environment';
import NotificationBox from '../../components/layout/NotificationBox';
import { checkTrialHasExpired, checkTrialHasStarted } from '../../components/utilities/TrialUtility';
import ColorContext from '../../context/ColorContext';

type MatchParams = {
  trialId: string;
};

const DigitalSurveyMenu: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams<MatchParams>();
  const trial = useSelector((store: RootState) => store.trial.trial);
  const [enrollment, setEnrollment] = useState<Enrollment>();
  const [hasEnrollmentFetched, setHasEnrollmentFetched] = useState<boolean>(false);
  const [surveys, setSurveys] = useState<DigitalSurvey[]>();
  const [exerciseExistance, setExerciseExistance] = useState<boolean>(false);
  const [surveyExistance, setSurveyExistance] = useState<boolean>(false);
  const [trialHasExpired, setTrialHasExpired] = useState<boolean>(false);
  const [trialHasStarted, setTrialHasStarted] = useState<boolean>(false);
  const { trialId } = params;

  useEffect(() => {
    const id = Number(trialId);
    ApiClient.getUserEnrollmentByTrial(id).then((e) => {
      setHasEnrollmentFetched(true);
      if (!e) {
        return;
      }
      setEnrollment(e);
    });
    dispatch(fetchTrial(id));
    ApiClient.getDigitalSurveyByTrialId(id).then((ds) => {
      setSurveys(ds);
    });
  }, [trialId, dispatch]);

  useEffect(() => {
    const setExerciseAvailibility = async () => {
      if (surveys) {
        const surveyDetailPromise = surveys.map((s) => ApiClient.fetchDigitalSurvey(s.id));
        const surveyDetails = await Promise.all(surveyDetailPromise);
        surveyDetails.forEach((sd) => {
          if (sd.exercise && sd.digitalSections?.length) {
            setExerciseExistance(true);
          } else if (!sd.exercise && sd.digitalSections?.length) {
            setSurveyExistance(true);
          }
        });
      }
    };

    setExerciseAvailibility();
  }, [surveys]);

  useEffect(() => {
    if (!hasEnrollmentFetched || !trial?.endDate) {
      return;
    }
    setTrialHasExpired(checkTrialHasExpired(trial));
    setTrialHasStarted(checkTrialHasStarted(trial));
  }, [hasEnrollmentFetched, trial]);

  const onCardClick = (id: CardIdType) => {
    switch (id) {
      case 'exercise': {
        const exerciseSurvey = surveys?.filter((s) => !!s.exercise);
        if (exerciseSurvey?.[0]?.id !== undefined) {
          navigate(`/digitaltrial/${trial?.id}/survey/${exerciseSurvey?.[0]?.id}/antibodies`);
        }
        break;
      }
      case 'survey': {
        const survey = surveys?.filter((s) => !s.exercise);
        if (survey?.[0]?.id !== undefined) {
          navigate(`/digitaltrial/${trial?.id}/survey/${survey?.[0]?.id}/antibodies`);
        }
        break;
      }
      case 'manual': {
        downloadFromContainer(MANUALS_CONTAINER_NAME, trial?.manual);
        break;
      }
      default:
    }
  };

  if (!hasEnrollmentFetched) {
    return null;
  }

  if (!enrollment) {
    return (
      <NotificationBox error>
        You are not allowed to access this content.
      </NotificationBox>
    );
  }

  const drtIsReady = (surveyExistance || exerciseExistance) && trialHasStarted;
  return (
    <ColorContext.Provider value="tertiary">
      <ContentBox title="Digital Readout Test" elevation={0} color="tertiary">
        <Box sx={{ mb: 2 }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link color="textPrimary" to={`/digitalTrial/${trialId}/menu`}>Menu</Link>
          </Breadcrumbs>
        </Box>
        <Typography variant="h4">{trial?.name}</Typography>
        <Container maxWidth="md" sx={{ mt: 9 }}>
          {trialHasExpired ? (
            <Box sx={{ mb: 5 }}>
              <Typography>
                The Digital Readout Test has expired. You may not submit any unfinished submission
              </Typography>
            </Box>
          ) : (
            <Box sx={{ mb: 5 }}>
              {
                !drtIsReady ? (
                  <Typography>
                    The Digital Readout Test is still in preparation. Please try again later.
                  </Typography>
                ) : (
                  <Typography>
                    {
                      exerciseExistance && (
                        'Exercise allows you to try on the available samples of Digital Readout Test. '
                      )
                    }
                    {
                      surveyExistance && (
                        'Select Readout Test to start your trial.'
                      )
                    }
                  </Typography>
                )
              }
            </Box>
          )}
          {
            drtIsReady && (
              <Grid container spacing={4} justifyContent="center">
                {
                  trial && trial.manual && (
                    <Grid item xs={12} sm={6} md={4}>
                      <SelectionCard
                        MuiIcon={ManualLogo}
                        text="Manual"
                        id="manual"
                        onCardClick={onCardClick}
                      />
                    </Grid>
                  )
                }
                {
                  exerciseExistance && (
                    <Grid item xs={12} sm={6} md={4}>
                      <SelectionCard
                        MuiIcon={ExerciseLogo}
                        text="Exercise"
                        id="exercise"
                        onCardClick={onCardClick}
                      />
                    </Grid>
                  )
                }
                {
                  surveyExistance && (
                    <Grid item xs={12} sm={6} md={4}>
                      <SelectionCard
                        MuiIcon={TestLogo}
                        text="Readout Test"
                        id="survey"
                        onCardClick={onCardClick}
                      />
                    </Grid>
                  )
                }
              </Grid>
            )
          }
        </Container>
      </ContentBox>
    </ColorContext.Provider>
  );
};

export default DigitalSurveyMenu;
